import { css, html, LitElement } from "lit";
import { property, query } from "lit/decorators.js";
import { BaseLitElement } from "./BaseLitElement";

export class TrackingElement extends LitElement {
  @property({ type: String, reflect: true }) tracking = "";
  @property({ type: String, attribute: "tracking-category", reflect: true })
  trackingCategory?: string;
  @property({ type: String, attribute: "tracking-label", reflect: true }) trackingLabel?: string;
  @property({ type: Boolean, attribute: "sneak" }) sneak = false;

  @query(".top-marker") private topMarker?: HTMLElement;
  @query(".bottom-marker") private bottomMarker?: HTMLElement;

  private _observer: IntersectionObserver | null = null;
  private _eventSent: Set<string> = new Set();
  private _disposers: Map<string, () => void> = new Map();
  private _topInView = false;
  private _bottomInView = false;
  private _paused = false;

  disconnectedCallback() {
    this._dispose();
    super.disconnectedCallback();
  }

  firstUpdated() {
    if (this._paused) return;
    this._setEventHandler();
  }

  /** 表示・クリック・発火は 1 つの要素につき 1 回だけ送る */
  private _eventTrack(action: string) {
    if (this._eventSent.has(action)) return;
    this._send(action);
    this._eventSent.add(action);
  }

  private _send(action: string, extra: Record<string, string> = {}) {
    const params = {
      event_category: this.trackingCategory,
      event_label: this.trackingLabel,
      ...extra,
    };
    if (import.meta.env.DEV) {
      console.log("eventTrack", action, params);
    } else {
      // @ts-expect-error
      window.gtag?.("event", action, params);
    }
  }

  private _dispose() {
    this._disposers.forEach((disposer) => {
      disposer();
    });
  }

  private _setEventHandler() {
    const targets = this.tracking.split(",").map((t) => t.trim());
    if (!this.tracking) return;

    if (targets.includes("click")) {
      this.addEventListener("click", this._handleClick);
      this._disposers.set("click", () => this.removeEventListener("click", this._handleClick));
    }

    if (targets.includes("view")) {
      this._observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            // 要素の上部が画面内に入ったかどうか
            if (!this._topInView && entry.target === this.topMarker)
              this._topInView = entry.isIntersecting;
            // 要素の下部が画面内に入ったかどうか
            else if (!this._bottomInView && entry.target === this.bottomMarker)
              this._bottomInView = entry.isIntersecting;

            // 両方のマーカーが画面内に入った場合
            if (this._topInView && this._bottomInView) this._handleView();
          });
        },
        {
          root: null,
          rootMargin: "0px",
          threshold: 1,
        },
      );

      if (this.topMarker && this.bottomMarker) {
        this._observer.observe(this.topMarker);
        this._observer.observe(this.bottomMarker);
        this._disposers.set("view", () => {
          this._observer?.disconnect();
        });
      }
    }
  }

  private _handleClick() {
    this._eventTrack("click");
  }

  private _handleView() {
    this._eventTrack("view");
  }

  /**
   * `tracking` 属性に関係なく、操作を送る (iframe の中から知らされた操作など)。繰り返しうる操作
   * なので、表示・クリックと違って起きるたびに送る。`event_category` / `event_label` は表示・クリックと
   * 同じこの要素の属性のまま (同じ要素のイベントで意味を変えない)。操作の側のラベル (どのクーポンか等) は
   * `embed_label` で別に送る (GA4 で集計するにはカスタムディメンションの登録が要る)
   */
  protected trackAction(action: string, embedLabel?: string) {
    this._send(action, embedLabel ? { embed_label: embedLabel } : {});
  }

  protected _fireTriggerEvent() {
    if (
      this.tracking
        .split(",")
        .map((t) => t.trim())
        .includes("trigger")
    ) {
      this._eventTrack("trigger");
    }
  }

  protected renderTopMarker() {
    return html`<div
      class="top-marker absolute left-0 right-0 top-0 h-[1px] overflow-hidden"
    ></div>`;
  }

  protected renderBottomMarker() {
    return html`<div
      class="bottom-marker absolute left-0 right-0 bottom-0 h-[1px] overflow-hidden"
    ></div>`;
  }

  protected pauseTracking() {
    this._paused = true;
    this._dispose();
  }

  protected resumeTracking() {
    this._paused = false;
    this._setEventHandler();
  }

  static styles = [
    ...BaseLitElement.styles,
    css`
      :host {
        position: relative;
        display: block;
      }
    `,
  ];
}
